import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MaskedLine } from "../components/Motion";
import { AboutSection, ExpertiseGrid, EducationBlock, AchievementsSection } from "../sections/About";
import { ProjectsSection, ProcessSection, MetricsSection } from "../sections/Work";
import { ServicesSection, TestimonialsSection } from "../sections/Services";
import { BlogSection, ResourcesSection, SpeakingSection } from "../sections/Insights";
import { FAQSection, ContactSection } from "../sections/Contact";

const PageShell = ({ overline, title, seoTitle, children, testId }) => {
  useEffect(() => { document.title = `${seoTitle} - Yash Bhatt`; }, [seoTitle]);
  return (
    <main data-testid={testId} className="pt-16">
      <header className="relative overflow-hidden border-b border-border">
        <div className="mesh-glow w-[500px] h-[500px] bg-violet-600/15 -top-40 -right-32" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <li><Link to="/" className="hover:text-secondary transition-colors duration-200">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-secondary">{seoTitle}</li>
            </ol>
          </nav>
          <MaskedLine>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-secondary mb-5">{overline}</p>
          </MaskedLine>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.15] pb-2 overflow-visible">
            <MaskedLine delay={0.15}>{title}</MaskedLine>
          </h1>
        </div>
      </header>
      {children}
    </main>
  );
};

export const AboutPage = () => (
  <PageShell testId="about-page" seoTitle="About" overline="About"
    title={<>The consultant behind <span className="text-gradient">enterprise transformation</span></>}>
    <AboutSection full />
    <AchievementsSection />
    <ExpertiseGrid />
    <EducationBlock />
  </PageShell>
);

export const ProjectsPage = () => (
  <PageShell testId="projects-page" seoTitle="Projects" overline="Case Studies"
    title={<>Case studies in <span className="text-gradient">measurable transformation</span></>}>
    <ProjectsSection full hideHeading />
    <MetricsSection />
  </PageShell>
);

export const ServicesPage = () => (
  <PageShell testId="services-page" seoTitle="Services" overline="Services"
    title={<>Engagements built around <span className="text-gradient">your business problem</span></>}>
    <ServicesSection full hideHeading />
    <ProcessSection />
    <TestimonialsSection />
  </PageShell>
);

export const BlogPage = () => (
  <PageShell testId="blog-page" seoTitle="Blog" overline="Insights"
    title={<>Field notes on <span className="text-gradient">enterprise AI</span></>}>
    <BlogSection full hideHeading />
  </PageShell>
);

export const ResourcesPage = () => (
  <PageShell testId="resources-page" seoTitle="Resources" overline="Resources" title={<>Tools I use in <span className="text-gradient">real engagements</span></>}>
    <ResourcesSection hideHeading />
  </PageShell>
);

export const SpeakingPage = () => (
  <PageShell testId="speaking-page" seoTitle="Speaking" overline="Speaking" title={<>Talks, podcasts & <span className="text-gradient">workshops</span></>}>
    <SpeakingSection hideHeading />
  </PageShell>
);

export const ContactPage = () => (
  <PageShell testId="contact-page" seoTitle="Contact" overline="Contact" title={<>Start the <span className="text-gradient">conversation</span></>}>
    <ContactSection embedCalendly hideHeading />
    <FAQSection />
  </PageShell>
);

const LegalShell = ({ title, testId, children }) => (
  <PageShell testId={testId} seoTitle={title} overline="Legal" title={title}>
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 space-y-6 text-sm text-muted-foreground leading-relaxed">
      {children}
    </div>
  </PageShell>
);

export const PrivacyPage = () => (
  <LegalShell title="Privacy Policy" testId="privacy-page">
    <p>Last updated: June 2026.</p>
    <p>This website collects only the information you voluntarily provide through the contact form and newsletter signup: your name, email address, company, and message. This data is used solely to respond to your inquiry or deliver the newsletter, and is never sold or shared with third parties.</p>
    <p>Contact form submissions are transmitted securely and stored for correspondence purposes. You may request deletion of your data at any time by emailing contact@bhattyash.com.</p>
    <p>This site does not use invasive tracking. Standard, privacy-respecting analytics may be used to understand aggregate traffic patterns.</p>
  </LegalShell>
);

export const TermsPage = () => (
  <LegalShell title="Terms of Service" testId="terms-page">
    <p>Last updated: June 2026.</p>
    <p>The content on this website - including articles, frameworks, templates, and case studies - is provided for informational purposes and does not constitute professional advice for your specific situation. Formal engagements are governed by individual consulting agreements.</p>
    <p>All original content is © Yash Bhatt. You may share and reference it with attribution; commercial reproduction requires written permission.</p>
    <p>Case study metrics reflect specific engagements and are not guarantees of future results.</p>
  </LegalShell>
);

export const NotFoundPage = () => {
  useEffect(() => { document.title = "404 - Yash Bhatt"; }, []);
  return (
    <main data-testid="not-found-page" className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden">
      <div className="mesh-glow w-[500px] h-[500px] bg-violet-600/20 top-1/4 left-1/4" aria-hidden="true" />
      <div className="relative">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-secondary">Error 404</p>
        <h1 className="mt-4 font-display text-6xl sm:text-7xl font-medium tracking-tighter text-gradient">Lost in the data.</h1>
        <p className="mt-6 text-muted-foreground max-w-md leading-relaxed">This page doesn't exist - but the insights do. Let's get you back on track.</p>
        <Link to="/" data-testid="not-found-home-link" className="mt-8 inline-flex h-12 items-center px-8 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-200">
          Back to Home
        </Link>
      </div>
    </main>
  );
};
